import { Color, Spacing } from '@persona-id/design-tokens';
import { CheckIcon, ChevronDownIcon, ChevronRightIcon } from '@persona-id/design-tokens/icons/16px';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import type { AdditionalPhotos } from 'dashboard/components/ChecksTable';
import ChecksTableRowMetadata from 'dashboard/components/ChecksTable/ChecksTableRowMetadata';
import { computeCheckMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata';
import { TextWithDashedUnderline } from 'dashboard/components/Typography';
import { TableTD, TableTR } from 'dashboard/layouts/TableLayout';
import { Check, CheckCategory, CheckStatus, MatchRequirement } from 'dashboard/models/check';
import { isAlphaCheck, isBetaCheck, isSunsetCheck } from 'dashboard/models/verification-check';
import Sticker from 'design-system/components/Sticker/Sticker';
import Tooltip from 'design-system/components/Tooltip';
import { CHECK_DISPLAY_CONFIG } from 'lib/constants/check';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';

interface ChecksTableRowProps {
  check: Check;
  additionalPhotos?: AdditionalPhotos;
}

const ChecksTableRow = ({ check, additionalPhotos }: ChecksTableRowProps) => {
  const checkConfig = CHECK_DISPLAY_CONFIG[check.name];
  const [isExpanded, setIsExpanded] = useState(false);

  // Note: When a row represents a failed required check, we expand it by default
  useEffect(() => {
    if (check.status === CheckStatus.Failed && check.requirement === MatchRequirement.Required) {
      setIsExpanded(true);
    }
  }, []);

  const hasPermissionsToViewIdBarcodeDetection = usePermission(
    Permission.VerificationGovIdNumberView,
  );

  const checkMetadataProps = computeCheckMetadata(
    check,
    hasPermissionsToViewIdBarcodeDetection,
    additionalPhotos?.frontPhotoUrl,
    additionalPhotos?.backPhotoUrl,
  );

  if (
    checkConfig == null ||
    checkConfig.category == null ||
    (check.status === CheckStatus.NotApplicable &&
      !(
        check.requirement === MatchRequirement.Required ||
        checkMetadataProps.showReasonsIfVerificationNotApplicable
      ))
  ) {
    return null;
  }

  // If the check is set to show only on fail, and it's not failed, then ignore it
  if (checkConfig.showOnlyOnFail && check.status !== CheckStatus.Failed) {
    return null;
  }

  let isExpandable = true;
  if (check['redacted-at']) {
    isExpandable = false;
  } else if (checkMetadataProps.hideIfVerificationPassed && check.status === CheckStatus.Passed) {
    isExpandable = false;
  } else if (check.status === CheckStatus.NotApplicable) {
    // This is because we currently always show a reason for N/A checks (we fall back to "This check was not run.")
    // We explicitly check for this case, because otherwise the metadata being empty would cause isExpandable to be false
    isExpandable = true;
  } else if (
    // This is because we currently always expand failed required checks, thus they should be expandable.
    check.status === CheckStatus.Failed &&
    check.requirement === MatchRequirement.Required &&
    check.reasons &&
    check.reasons.length > 0
  ) {
    isExpandable = true;
  } else if (
    checkMetadataProps.showReasonsIfVerificationFailed &&
    check.status === CheckStatus.Failed &&
    check.reasons &&
    check.reasons.length > 0
  ) {
    isExpandable = true;
  } else if (checkMetadataProps.metadata.length === 0) {
    isExpandable = false;
  }

  return (
    <>
      <TableTR aria-expanded={isExpandable ? isExpanded : undefined}>
        <ChecksTableTD hasBorderBottom={!isExpanded}>
          <ChecksTableTDContents hasLeftPadding={!isExpandable}>
            {isExpandable && !isExpanded && (
              <ChevronRightIcon onClick={() => setIsExpanded(true)} />
            )}
            {isExpandable && isExpanded && <ChevronDownIcon onClick={() => setIsExpanded(false)} />}
            {(() => {
              switch (check.status) {
                case CheckStatus.Passed:
                  return <Sticker variant='success'>Passed</Sticker>;
                case CheckStatus.Failed:
                  return <Sticker variant='danger'>Failed</Sticker>;
                default:
                  return <Sticker variant='basic'>N/A</Sticker>;
              }
            })()}
          </ChecksTableTDContents>
        </ChecksTableTD>
        <ChecksTableTD hasBorderBottom={!isExpanded}>
          <ChecksTableTDContents>
            {checkConfig.description ? (
              <Tooltip
                side='right'
                trigger={<TextWithDashedUnderline>{checkConfig.name}</TextWithDashedUnderline>}
              >
                <ChecksTableTooltip>{checkConfig.description}</ChecksTableTooltip>
              </Tooltip>
            ) : (
              checkConfig.name
            )}
            {isAlphaCheck(check.name) && (
              <Tooltip trigger={<Sticker variant='caution'>Alpha</Sticker>}>
                This feature is in alpha. Please don&apos;t rely on it for critical checks or
                automation. Manual review is suggested. Please share your feedback with us.
              </Tooltip>
            )}
            {isBetaCheck(check.name) && (
              <Tooltip trigger={<Sticker variant='caution'>Beta</Sticker>}>
                This feature is in beta. Please don&apos;t rely on it for critical checks or
                automation. Manual review is suggested. Please share your feedback with us.
              </Tooltip>
            )}
            {isSunsetCheck(check.name) && (
              <Tooltip trigger={<Sticker variant='caution'>Sunset</Sticker>}>
                This feature is being sunset. Please reach out to Persona for more info and
                alternatives.
              </Tooltip>
            )}
          </ChecksTableTDContents>
        </ChecksTableTD>
        <ChecksTableTD hasBorderBottom={!isExpanded}>
          {checkConfig.category === CheckCategory.Fraud
            ? 'Fraud'
            : checkConfig.category === CheckCategory.UserActionRequired
              ? 'User behavior'
              : checkConfig.category === CheckCategory.Validity
                ? 'Validity'
                : checkConfig.category === CheckCategory.Biometrics
                  ? 'Biometrics'
                  : ''}
        </ChecksTableTD>
        <ChecksTableTD hasBorderBottom={!isExpanded}>
          {(check.requirement === MatchRequirement.Required ||
            check.requirement === MatchRequirement.RequiresRetry) && (
            <CheckIcon data-test={`checks-table-v2-row__${check.name}-required-icon`} />
          )}
        </ChecksTableTD>
      </TableTR>
      {isExpanded && (
        <TableTR disableHoverBackground={true}>
          <TableTD colSpan={4}>
            <ChecksTableRowMetadata id={check.name} check={check} {...checkMetadataProps} />
          </TableTD>
        </TableTR>
      )}
    </>
  );
};

export const ChecksTableTD = styled(TableTD)<{ hasBorderBottom: boolean }>`
  border-bottom: ${({ hasBorderBottom }) =>
    hasBorderBottom ? `1px solid ${Color.BorderDivider}` : 0};
`;

export const ChecksTableTDContents = styled.div<{ hasLeftPadding?: boolean }>`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[2]};
  align-items: center;

  // For alignment due to the ChevronDownIcon or ChevronRightIcon in the Status column
  ${({ hasLeftPadding }) => hasLeftPadding && `padding-left: ${Spacing[5]};`}

  > svg {
    cursor: pointer;
  }
`;

export const ChecksTableTooltip = styled.div`
  max-width: 300px;
`;

export default ChecksTableRow;
